import * as React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsWrapper } from '../Tabs';
import { Select, SelectItem } from '../Select';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { ResponsiveTabsListProps, ResponsiveTabsProps } from './types';

export const ResponsiveTabsList = React.forwardRef<
  HTMLDivElement,
  ResponsiveTabsListProps
>(({ tabs, value, onValueChange, tabListProps }, ref) => {
  const isMobile = !useTailwindBreakpoint('md');

  if (isMobile) {
    return (
      <div ref={ref} className='eye:w-full eye:max-w-[350px]'>
        <Select
          value={value}
          onValueChange={onValueChange}
          slotProps={{
            trigger: { className: 'eye:rounded-full eye:bg-white' },
          }}
        >
          {tabs.map((tab, index) => (
            <SelectItem key={tab.key || index} value={String(index)}>
              <div className='eye:body-s eye:flex eye:items-center eye:justify-center'>
                {tab.title}
              </div>
            </SelectItem>
          ))}
        </Select>
      </div>
    );
  }

  return (
    <TabsList ref={ref} {...tabListProps}>
      {tabs.map((tab, index) => (
        <TabsTrigger key={tab.key || index} value={String(index)}>
          {tab.title}
        </TabsTrigger>
      ))}
    </TabsList>
  );
});

ResponsiveTabsList.displayName = 'ResponsiveTabsList';

export const ResponsiveTabs = React.forwardRef<
  React.ElementRef<typeof Tabs>,
  ResponsiveTabsProps
>(
  (
    {
      tabs,
      tabIndex,
      defaultTabIndex = 0,
      onTabChange,
      tabListProps,
      headerRight,
      ...props
    },
    ref,
  ) => {
    const isControlled = tabIndex !== undefined;

    const [internalIndex, setInternalIndex] = React.useState(defaultTabIndex);

    const currentIndex = String(isControlled ? tabIndex : internalIndex);

    const handleValueChange = React.useCallback(
      (nextValue: string) => {
        const nextIndex = Number(nextValue);

        if (!isControlled) {
          setInternalIndex(nextIndex);
        }

        onTabChange?.(nextIndex);
      },
      [isControlled, onTabChange],
    );

    return (
      <TabsWrapper
        ref={ref}
        tabs={tabs}
        value={currentIndex}
        onValueChange={handleValueChange}
        {...props}
      >
        {headerRight ? (
          <div className='eye:flex eye:flex-wrap eye:items-center eye:justify-between eye:gap-xs'>
            <ResponsiveTabsList
              tabs={tabs}
              value={currentIndex}
              onValueChange={handleValueChange}
              tabListProps={tabListProps}
            />
            {headerRight}
          </div>
        ) : (
          <ResponsiveTabsList
            tabs={tabs}
            value={currentIndex}
            onValueChange={handleValueChange}
            tabListProps={tabListProps}
          />
        )}
      </TabsWrapper>
    );
  },
);

ResponsiveTabs.displayName = 'ResponsiveTabs';
